/** Where the paywall / gate was opened from — matches mobile `PaywallSource`. */
export const PAYWALL_SOURCES = [
  'sticker_pack',
  'sticker_scene',
  'nab',
  'crossword_picker',
  'crossword_play',
  'basic_vocabulary',
  'reduplication',
  'storybook',
  'settings',
  'term_sheet',
  'flashcard',
  'hmong_village_bike',
  'hmong_village_rewards',
  'sentence_practice',
  'build_sentence',
] as const;

export type PaywallSource = (typeof PAYWALL_SOURCES)[number];
export type PaywallSurface = 'gate' | 'marketing';

export type PaywallAnalyticsContext = {
  source: PaywallSource;
  surface: PaywallSurface;
  content?: string;
  signed_in?: boolean;
};

export type PaywallGateCopy = {
  source: PaywallSource;
  message: string;
  heroEmoji?: string;
};

const SOURCE_SET = new Set<string>(PAYWALL_SOURCES);

export function parsePaywallSource(value: unknown): PaywallSource | undefined {
  return typeof value === 'string' && SOURCE_SET.has(value) ? (value as PaywallSource) : undefined;
}

export function subscribePath(source: PaywallSource, content?: string): string {
  const params = new URLSearchParams({ source });
  if (content) params.set('content', content);
  return `/app/subscribe?${params.toString()}`;
}

export function paywallEventProps(
  ctx: PaywallAnalyticsContext,
  extra?: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean> {
  const props: Record<string, string | number | boolean> = {
    source: ctx.source,
    surface: ctx.surface,
  };
  if (ctx.content) props.content = ctx.content;
  if (ctx.signed_in != null) props.signed_in = ctx.signed_in;
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (value == null) continue;
      props[key] = value;
    }
  }
  return props;
}

/** Compact gate copy — same strings as mobile `SubscriptionGateModal` call sites. */
export function flashcardGateCopy(title: string): PaywallGateCopy {
  return {
    source: 'flashcard',
    message: `Subscribe to unlock ${title} and more premium content.`,
    heroEmoji: '🃏',
  };
}

export function sheetGateCopy(slug: string): PaywallGateCopy {
  if (slug === 'reduplication') {
    return {
      source: 'reduplication',
      message: 'Subscribe to unlock Reduplication and more premium content.',
      heroEmoji: '📚',
    };
  }
  return {
    source: 'term_sheet',
    message: 'Subscribe to unlock this term sheet and more premium content.',
  };
}

export function storybookGateCopy(slug?: string): PaywallGateCopy {
  if (slug === 'kuv-sov-sov') {
    return {
      source: 'storybook',
      message: 'Subscribe to unlock Kuv Sov Sov and more premium content.',
      heroEmoji: '📖',
    };
  }
  return {
    source: 'storybook',
    message: 'Subscribe to unlock this storybook and more premium content.',
    heroEmoji: '📖',
  };
}

export function crosswordPickerGateCopy(): PaywallGateCopy {
  return {
    source: 'crossword_picker',
    message: 'Subscribe to unlock crosswords and archived puzzles.',
    heroEmoji: '🧩',
  };
}

export function crosswordPlayGateCopy(): PaywallGateCopy {
  return {
    source: 'crossword_play',
    message: 'Subscribe to unlock daily crosswords and archived puzzles.',
    heroEmoji: '🧩',
  };
}

export function nabGateCopy(): PaywallGateCopy {
  return {
    source: 'nab',
    message: 'Subscribe to play Nab and unlock more premium content.',
    heroEmoji: '🐍',
  };
}

export function stickerPackGateCopy(): PaywallGateCopy {
  return {
    source: 'sticker_pack',
    message: 'Subscribe to unlock these stickers and more!',
  };
}

export function stickerSceneGateCopy(): PaywallGateCopy {
  return {
    source: 'sticker_scene',
    message: 'Subscribe to place premium stickers on your scenes!',
  };
}

export function sentencePracticeGateCopy(kind: 'multiple_choice' | 'builder' = 'builder'): PaywallGateCopy {
  return {
    source: 'sentence_practice',
    message: 'Subscribe to unlock more practice sentences and keep building your Hmong.',
    heroEmoji: kind === 'multiple_choice' ? '📝' : '🧩',
  };
}

export function buildSentenceGateCopy(): PaywallGateCopy {
  return {
    source: 'build_sentence',
    message: 'Subscribe to play a new random set of sentences each round.',
    heroEmoji: '🧩',
  };
}
